/**
 * Stringifies an object of attributes to a search params string.
 * It encodes the keys and values of the object to be URI safe.
 *
 * @param attributes - The attributes to stringify.
 * @param options - The options to use when stringifying the attributes.
 * @returns A stringified version of the attributes.
 *
 * @example
 * ```ts
 * attributesToQueryString({ name: 'John', age: 30, coupon: 'thing38489[901+=04]' }); // 'name=John&age=30&coupon=thing38489%5B901%2B%3D04%5D'
 * attributesToQueryString({ name: 'John', age: 30 }, { delimiter: ';' }); // 'name=John;age=30'
 * attributesToQueryString({ name: 'John', age: 30 }, { addQueryPrefix: true }); // '?name=John&age=30'
 * attributesToQueryString({ coupon: 'thing38489[901+=04]' }, { skipEncoding: true }); // 'coupon=thing38489[901+=04]'
 * ```
 */

export function attributesToQueryString(
  attributes: Record<string, string | number | boolean | undefined>,
  {
    delimiter = '&',
    addQueryPrefix = false,
    skipEncoding = false,
  }: {
    /** The delimiter to use between key-value pairs. @defaultValue `&` */
    delimiter?: string;
    /** Whether to add a query prefix (i.e. `?`) to the string. @defaultValue false */
    addQueryPrefix?: boolean;
    /** Whether to skip encoding the keys and values of the attributes. This is
     * useful when we don't want to encode the keys and values of the attributes
     * for example when we want to pass a query string to an source set of an
     * image.
     *
     * @defaultValue false
     */
    skipEncoding?: boolean;
  } = {}
): string {
  const params = Object.entries(attributes)
    .filter(
      (entry): entry is [string, string | number | boolean] =>
        typeof entry[1] !== 'undefined'
    )
    .map(([key, value]) =>
      skipEncoding
        ? `${key}=${value}`
        : `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join(delimiter);
  return addQueryPrefix ? `?${params}` : params;
}
